import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ListChecks, Trash2, Users, Loader2 } from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Card, Input, Button, Modal } from '../../Components/ui';
import { useConfirm, useToast } from '../../Components/Feedback';
import { useApi } from '../../api/useApi';

// Tela inicial de "Listas" — agrupamentos nomeados de alunos por instituição
// (ex.: "Aniversariantes de Setembro", "Vai no passeio"), estilo Apple Notas:
// cartões com o título, clicar abre a lista completa (ver ListaDetalhe.js).
function Listas() {
  const navigate = useNavigate();
  const { institutionId } = useInstitution();
  const { token } = useAuth();
  const { podeCriar, podeExcluir } = usePermissao('/listas');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();

  const api = useApi();

  const { data: listas = [], isLoading } = useQuery({
    queryKey: ['listas', institutionId],
    queryFn: async () => {
      const res = await api.get('/api/listas');
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const recarregar = () => queryClient.invalidateQueries({ queryKey: ['listas', institutionId] });

  // --- Criar ---
  const [modalAberto, setModalAberto] = useState(false);
  const [tituloForm, setTituloForm] = useState('');
  const [salvando, setSalvando] = useState(false);

  const criar = async (e) => {
    e.preventDefault();
    const titulo = tituloForm.trim();
    if (!titulo) return;
    setSalvando(true);
    try {
      const res = await api.post('/api/listas', { titulo });
      const criado = res?.data ?? res;
      setModalAberto(false);
      setTituloForm('');
      toast.success(res?.message || 'Lista criada com sucesso.');
      recarregar();
      if (criado?.id) navigate(`/listas/${criado.id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  // --- Apagar ---
  const apagar = async (e, lista) => {
    e.stopPropagation();
    if (
      !(await confirm(
        `Apagar a lista "${lista.titulo}"? Isso não afeta o cadastro dos alunos, só remove o agrupamento.`,
        { title: 'Apagar lista', confirmText: 'Apagar' },
      ))
    )
      return;
    try {
      const res = await api.del(`/api/listas/${lista.id}`);
      toast.success(res?.message || 'Lista apagada com sucesso.');
      recarregar();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Agrupamentos de alunos — clique num título pra abrir.
          </p>
          {podeCriar && (
            <Button variant="primary" icon={Plus} onClick={() => setModalAberto(true)}>
              Nova Lista
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : listas.length === 0 ? (
          <div className="text-center py-16 text-gray-400 dark:text-gray-500">
            <ListChecks className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-700" />
            <p className="text-sm">Nenhuma lista criada ainda.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {listas.map((lista) => (
              <Card
                key={lista.id}
                className="cursor-pointer hover:border-blue-200 dark:hover:border-blue-800 transition-colors group"
              >
                <div
                  onClick={() => navigate(`/listas/${lista.id}`)}
                  className="flex items-start justify-between gap-2"
                >
                  <div className="min-w-0">
                    <h3
                      className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate"
                      translate="no"
                    >
                      {lista.titulo}
                    </h3>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1">
                      <Users className="w-3 h-3" /> {lista.total_alunos} aluno
                      {lista.total_alunos === 1 ? '' : 's'}
                    </p>
                  </div>
                  {podeExcluir && (
                    <button
                      onClick={(e) => apagar(e, lista)}
                      title="Apagar lista"
                      className="w-7 h-7 flex-shrink-0 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Modal
        isOpen={modalAberto}
        onClose={() => setModalAberto(false)}
        title="Nova Lista"
        maxWidth="sm"
      >
        <form onSubmit={criar} className="space-y-4">
          <Input
            label="Título"
            value={tituloForm}
            onChange={(e) => setTituloForm(e.target.value)}
            placeholder="Ex.: Aniversariantes de Setembro"
            required
            autoFocus
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalAberto(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={salvando}>
              {salvando ? 'Criando...' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Listas;
